import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { AuthScaffold } from './AuthScaffold'
import { tokenErrorMessage } from './authErrors'
import { passwordProblem } from './password'
import { FxAlert, FxButton, FxTextField } from '@/components/fx'
import { authApi } from '@/lib/api'

/**
 * The second half of the reset, reached from the e-mail link.
 *
 * The gateway builds that link as `<frontend>/auth/password-reset/confirm
 * ?token=…` (`internal/auth/service.go`), so this screen has to answer on
 * that path as well as on the German one.
 */
export function PasswordResetConfirmPage() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const toLogin = (
    <p className="text-small">
      <Link className="text-primary-text" to="/login">
        Zurück zur Anmeldung
      </Link>
    </p>
  )

  if (!token) {
    return (
      <AuthScaffold
        title="Link ungültig"
        footer={
          <p className="text-small">
            <Link className="text-primary-text" to="/passwort-vergessen">
              Neuen Link anfordern →
            </Link>
          </p>
        }
      >
        <FxAlert tone="danger" className="text-left">
          Dieser Link enthält kein gültiges Merkmal. Er wurde vermutlich nicht vollständig kopiert.
        </FxAlert>
      </AuthScaffold>
    )
  }

  if (done) {
    return (
      <AuthScaffold title="Passwort geändert" footer={toLogin}>
        <FxAlert tone="success" className="text-left">
          Du kannst dich jetzt mit deinem neuen Passwort anmelden.
        </FxAlert>
      </AuthScaffold>
    )
  }

  return (
    <AuthScaffold
      title="Neues Passwort"
      subtitle="Wähle ein Passwort mit mindestens 8 Zeichen."
      footer={toLogin}
    >
      {error && (
        <FxAlert tone="danger" className="text-left">
          {error}
        </FxAlert>
      )}
      <form
        className="flex flex-col gap-7 text-left"
        onSubmit={(event) => {
          event.preventDefault()
          const problem = passwordProblem(password, repeat)
          if (problem) {
            setError(problem)
            return
          }
          setBusy(true)
          setError(null)
          void authApi
            .confirmPasswordReset(token, password)
            .then(() => setDone(true))
            .catch((cause) => setError(tokenErrorMessage(cause)))
            .finally(() => setBusy(false))
        }}
      >
        <FxTextField
          label="Neues Passwort"
          onWhite
          type="password"
          required
          minLength={8}
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <FxTextField
          label="Passwort wiederholen"
          onWhite
          type="password"
          required
          autoComplete="new-password"
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
        />
        <FxButton type="submit" block disabled={busy}>
          {busy ? 'Passwort wird gespeichert …' : 'Passwort speichern'}
        </FxButton>
      </form>
    </AuthScaffold>
  )
}
